import React, { useState, useRef } from "react";
import { getFirestore, doc, updateDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import toast from "react-hot-toast";
import { useProfile } from "../../context/ProfileContext";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TermsOfService202605 from "./TermsOfService202611";

const LegalMigrationModal = ({ artistData }) => {
  const { isDarkMode, refreshContext, switchProfile, userProfile } = useProfile();
  const navigate = useNavigate();
  const db = getFirestore();

  // --- FORM STATE ---
  const [legalName, setLegalName] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const scrollRef = useRef(null);

  if (!artistData || artistData.type !== 'artist') return null;

  const handleCancel = async () => {
    try {
      if (userProfile) {
        await switchProfile(userProfile);
        toast.success("Switched profiles");
        navigate("/profile");
      } else {
        console.warn("No user profile found, navigating to safety...");
        navigate("/");
      }
    } catch (error) {
      console.error("Escape failed:", error);
      navigate("/");
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 20) {
      setHasScrolledToBottom(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!legalName.trim() || !acceptedTerms) {
      toast.error("Please fill in your legal name and accept the terms.");
      return;
    }

    setLoading(true);
    try {
      const artistRef = doc(db, "artist_accounts", artistData.id);
      
      await updateDoc(artistRef, {
        legalName: legalName.trim(),
        legalRequirementStatus: "COMPLETED",
        legalAgreementVersion: "2.3-NOV-2026",
        updatedAt: serverTimestamp()
      });

      const historyRef = collection(db, "aos_profiles", artistData.id, "outreach_history");
      await addDoc(historyRef, {
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0],
        identifier: "Legacy Artist Creator Terms Update",
        version: "2.3-NOV-2026",
        responseType: "Legal Compliance",
        isAutomated: true
      });

      toast.success("Account verified. You're good to go!");
      await refreshContext();
      
    } catch (error) {
      console.error("Migration error:", error);
      toast.error("Failed to update legal status.");
    } finally {
      setLoading(false);
    }
  };

  const inputTheme = isDarkMode 
    ? "bg-zinc-800 border-zinc-700 text-white focus:border-[#ff00cc]" 
    : "bg-white border-slate-200 text-slate-900 focus:border-blue-500";

  return (
    /* ✅ 1. Added ${isDarkMode ? 'dark' : ''} to activate all Tailwind dark: variants */
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm overflow-y-auto ${isDarkMode ? 'dark' : ''}`}>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`max-w-2xl w-full p-6 md:p-8 my-auto shadow-2xl rounded-3xl border transition-all ${
          isDarkMode ? "bg-[#121212] border-zinc-800 text-white" : "bg-white border-slate-100 text-slate-900"
        }`}
      >
        <header className="mb-6 text-center">
          <h2 className={`text-2xl font-black italic tracking-tighter ${isDarkMode ? "text-white" : "text-slate-900"}`}>
            Action Required: Legal Update
          </h2>
          <p className="text-[#ff00cc] text-[10px] font-black uppercase tracking-widest mt-1">
            CREATOR TERMS UPDATE (V2.3)
          </p>
          <p className={`text-xs mt-3 px-4 leading-relaxed ${isDarkMode ? "text-zinc-400" : "text-slate-500"}`}>
            We&apos;ve streamlined our platform terms to give your music friction-free promotional reach. Please confirm your details and accept Version 2.3 to keep your profile active.
          </p>
        </header>

        {/* Identity banner profile avatar badge */}
        <div className="flex flex-col items-center mb-6">
          <div className="relative mb-2">
            <img 
              src={artistData?.photoURL || "https://via.placeholder.com/150"} 
              alt={artistData?.artistName}
              className="w-20 h-20 rounded-full object-cover border-4 border-[#ff00cc] shadow-lg shadow-[#ff00cc]/20"
            />
            <div className="absolute -bottom-1 -right-1 bg-[#ff00cc] p-1 rounded-full border-2 border-[#121212]">
              <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className={`text-xl font-black italic tracking-tighter ${isDarkMode ? "text-white" : "text-slate-900"}`}>
            {artistData?.artistName}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Identity Full Legal Name input */}
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-500 mb-2 block">Full Legal Name</label>
            <input 
              type="text" 
              value={legalName} 
              onChange={(e) => setLegalName(e.target.value)} 
              className={`w-full p-3 rounded-xl outline-none border text-sm font-semibold transition-all ${inputTheme}`} 
              required 
            />
          </div>

          {/* Terms Agreement Scroll Region */}
          <div>
            <label className="text-[10px] font-black uppercase text-zinc-500 block mb-2">
              Direct Licensing &amp; Indemnity Agreement
            </label>
            
            {/* ✅ 2. Explicitly setting text-zinc-100 in dark mode prevents dark-on-dark fallback */}
            <div 
              ref={scrollRef}
              onScroll={handleScroll}
              className={`h-48 overflow-y-scroll p-4 text-[10px] leading-relaxed rounded-xl border scrollbar-thin shadow-inner transition-colors ${
                isDarkMode 
                  ? "bg-zinc-950 border-zinc-800 text-zinc-100" 
                  : "bg-slate-50 border-slate-200 text-slate-800"
              }`}
            >
              <TermsOfService202605 />
              
              <div className={`py-4 text-center font-black tracking-widest text-[9px] uppercase ${
                isDarkMode ? "text-zinc-500" : "text-slate-400"
              }`}>
                --- End of Agreement ---
              </div>
            </div>
          </div>

          {/* Form Actions Drawer */}
          <div className="flex flex-col items-center gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input 
                type="checkbox" 
                disabled={!hasScrolledToBottom} 
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="w-5 h-5 rounded accent-[#ff00cc] cursor-pointer disabled:opacity-40"
              />
              <span className={`text-xs font-bold ${
                !hasScrolledToBottom 
                  ? "opacity-50" 
                  : isDarkMode ? "text-white" : "text-slate-900"
              }`}>
                I Agree to the Updated Terms of Service
              </span>
            </label>

            <button
              type="submit"
              disabled={loading || !acceptedTerms}
              className={`w-full p-4 font-black uppercase tracking-widest rounded-xl transition-all shadow-xl disabled:opacity-25 ${
                isDarkMode ? "bg-[#ff00cc] text-white" : "bg-blue-600 text-white"
              }`}
            >
              {loading ? "Syncing Identity Parameters..." : "Verify & Activate Profile"}
            </button>

            <button
              type="button"
              onClick={handleCancel}
              className={`w-full p-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                isDarkMode ? "text-zinc-500 hover:text-white" : "text-slate-400 hover:text-slate-900"
              }`}
            >
              Not now, switch back to My Profile
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default LegalMigrationModal;